import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, theme } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  ShoppingCartOutlined,
  AuditOutlined,
  WalletOutlined,
  LogoutOutlined,
  UserOutlined,
  InboxOutlined,
  ClockCircleOutlined,
  GiftOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import '../../index.css'

const { Header, Sider, Content } = Layout;

const MainLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [userFullName, setUserFullName] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    const fullName = `${userDetails.FirstName || ''} ${userDetails.LastName || ''}`.trim();
    setUserFullName(fullName || '');
  }, []);

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Anasayfa',
    },
    {
      key: 'reports',
      label: 'RAPORLAR',
      type: 'group',
      children: [
        {
          key: 'sales',
          icon: <ShoppingCartOutlined />,
          label: 'Satış',
          children: [
            {
              key: '/sales-summaries',
              label: 'Satış Özetleri',
            },
            {
              key: '/top-products-reports',
              label: 'En Çok/Az Satılan Ürünler',
            },
            {
              key: '/top-waiters-reports',
              label: 'Personel Performans',
            },
            {
              key: '/average-table-consumption-reports',
              label: 'Masa Ortalama Tüketim',
            }
          ],
        },
        {
          key: 'stock',
          icon: <InboxOutlined />,
          label: 'Stok',
          children: [
            {
              key: '/product-cost-sales-ratio-reports',
              label: 'Ürünlerin Maliyeti',
            },
            {
              key: '/critical-stock-reports',
              label: 'Kritik Stok',
            },
          ],
        },
        {
          key: 'cash',
          icon: <WalletOutlined />,
          label: 'Kasa',
          children: [
            {
              key: '/payment-reports',
              label: 'Kasa Raporları',
            },
          ],
        },
        {
          key: '/day-times-reports',
          icon: <ClockCircleOutlined />,
          label: 'Aktif Saat Raporu',
        },
        {
          key: 'instant',
          icon: <ClockCircleOutlined />,
          label: 'Anlık Raporlar',
          children: [
            {
              key: '/instant-reports',
              label: 'Müşteri Masa Raporları',
            },
            {
              key: '/staff-reports',
              label: 'Personel Sayısı',
            },
            {
              key: '/unpaid-reports',
              label: 'Cari Ödemeler',
            },
            {
              key: '/gift-reports',
              label: 'İkram',
            },
          ],
        }
      ],
    },
  ];

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userDetails');
    navigate('/login');
  };

  return (
    <Layout className="min-h-screen font-montserrat select-none">
      <Header
        style={{
          padding: 0,
          background: colorBgContainer,
        }}
        className="fixed top-0 left-0 w-full z-20 flex items-center justify-between px-4 shadow-sm h-16"
      >
        <div className="flex items-center gap-4">
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            className="text-xl"
          />
          <div className="flex items-center min-w-0 flex-1">
            <img src={`${process.env.PUBLIC_URL || ''}/ordermax-bulut-logo.png`} alt="OrderMAX Bulut" className="h-8 sm:h-12 object-contain object-left max-w-[140px] sm:max-w-[280px]" />
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-4">
          <span className="text-gray-600 font-medium text-sm sm:text-base">{userFullName}</span>
          <UserOutlined className="text-lg sm:text-xl text-gray-600" />
        </div>
      </Header>

      <Layout className="pt-16 h-[calc(100vh-64px)]">
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          breakpoint="lg"
          collapsedWidth={isMobile ? 0 : 80}
          width={240}
          className="fixed left-0 top-16 bottom-0 z-10 flex flex-col"
          style={{ height: 'calc(100vh - 64px)' }}
          onBreakpoint={(broken) => {
            setCollapsed(broken);
          }}
          onCollapse={(collapsed, type) => {
            if (type === 'responsive') {
              setCollapsed(collapsed);
            }
          }}
        >
          <div className="flex-1 overflow-y-auto overflow-x-hidden" style={{ paddingBottom: '80px', minHeight: 0, maxHeight: '100%' }}>
            <Menu
              theme="dark"
              mode="inline"
              selectedKeys={[location.pathname]}
              items={menuItems}
              onClick={({ key }) => {
                navigate(key);
                if (isMobile) {
                  setCollapsed(true);
                }
              }}
              className="border-r-0"
            />
          </div>
          {(collapsed && isMobile) ? null : (
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-[#141414] border-t border-gray-700 z-10">
              <Button
                type="text"
                icon={<LogoutOutlined />}
                onClick={handleLogout}
                className="w-full text-white hover:text-brand-light flex items-center justify-center gap-2"
              >
                {!collapsed && 'Çıkış Yap'}
              </Button>
            </div>
          )}
        </Sider>

        <Layout className={`transition-all duration-200 ${collapsed ? (isMobile ? 'ml-0' : 'ml-20') : 'ml-0 md:ml-[240px]'}`}>
          <Content
            style={{
              background: colorBgContainer,
              borderRadius: borderRadiusLG,
            }}
            className="p-4 sm:p-6 mx-2 sm:mx-4 my-2 sm:my-4 min-h-[calc(100vh-80px)]"
          >
            {children}
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default MainLayout;